import { DotsVerticalIcon, PlusCircleIcon } from "@heroicons/react/solid";
import { CollapsibleSection } from "components/collapsibleSection";
import { DropdownMenu } from "components/dropdownMenu";
import Input from "components/input";
import { Label } from "components/label";
import { TextEditor } from "components/textEditor";
import {
  IBuildEventPageForm,
  IEventSection,
  SupportedLanguage,
} from "interfaces";
import { get } from "lodash";
import { useState } from "react";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { LANGUAGES } from "utills/constants";
import { useTranslation } from "@refinedev/core";

interface IDetailFields extends IEventSection {
  id: string;
}

export const EventSection = ({
  isEdit,
  id,
}: {
  isEdit: boolean;
  id: string | null;
}) => {
  const { translate } = useTranslation();
  const [openSectionId, setOpenSectionId] = useState<string | null>(null);

  const {
    watch,
    control,
    getValues,
    getFieldState,
    formState: { errors },
  } = useFormContext<IBuildEventPageForm>(); // retrieve all hook methods

  const defaultLanguage = getValues("languages").includes(SupportedLanguage.EN)
    ? SupportedLanguage.EN
    : getValues("languages")[0] ?? "";
  const [selectedContentLanguage, setSelectedContentLanguage] =
    useState(defaultLanguage);

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "details",
  });

  const handleDrag = ({ source, destination }: any) => {
    if (destination) {
      move(source.index, destination.index);
    }
  };

  return (
    <div className="box-container p-4 sm:p-6">
      {!isEdit ? (
        (!getFieldState("details").isTouched || errors["details"]) && !id ? (
          <p>
            {translate("eventSetup.eventInfo.eventSection.note")}
          </p>
        ) : (
          <div className="flex flex-col gap-y-4">
            <div className="flex flex-row items-center gap-x-4">
              {translate("eventSetup.eventInfo.eventSection.viewContentByLanguage")}
              <div className="flex flex-row gap-x-1.5 p-1.5 w-fit rounded-full outline outline-solid outline-[1px] outline-brand-500">
                {getValues("languages").map((lang, index) => (
                  <img
                    key={lang}
                    src={`/images/flags/circle-${lang}.png`}
                    alt="Language Icon"
                    className={`w-4 shrink-0 cursor-pointer ${
                      lang !== selectedContentLanguage ? "opacity-20" : ""
                    }`}
                    onClick={() => setSelectedContentLanguage(lang)}
                  />
                ))}
              </div>
            </div>
            {getValues("details").map(
              (detail: IEventSection, index: number) => (
                <div className="border border-gray-300 rounded-lg" key={index}>
                  <CollapsibleSection
                    label={get(detail, `title.${selectedContentLanguage}`, "")}
                    className="shadow-none"
                    defaultOpen={false}
                    key={index}
                    isAnimated={false}
                  >
                    <div
                      className="px-4 py-4 reset-tw sm:px-6"
                      dangerouslySetInnerHTML={{
                        __html: `${get(
                          detail,
                          `description.${selectedContentLanguage}`,
                          ""
                        )}`,
                      }}
                    ></div>
                  </CollapsibleSection>
                </div>
              )
            )}
          </div>
        )
      ) : (
        <div>
          <DragDropContext onDragEnd={handleDrag}>
            <div className="grid sm:grid-cols-6 lg:gap-x-6">
              <Droppable droppableId="test-items">
                {(provided, snapshot) => (
                  <div
                    className="col-span-full"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {fields.map((field: IDetailFields, index: number) => (
                      <Draggable
                        key={`test[${index}]`}
                        draggableId={`item-${index}`}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            className={`border rounded-lg mb-4 ${
                              get(errors, `details.${index}`, "")
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                            key={field.id}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                          >
                            <CollapsibleSection
                              label={`${get(
                                watch("details")[index],
                                `title.${selectedContentLanguage}`
                              )}`}
                              customOpen={{
                                open: openSectionId === field.id,
                                onClick: () =>
                                  setOpenSectionId((prevId) =>
                                    prevId === field.id ? null : field.id
                                  ),
                              }}
                              isAnimated={false}
                              toolbar={
                                <DropdownMenu
                                  label={
                                    <DotsVerticalIcon className="w-4 h-4 text-brand-500" />
                                  }
                                  items={[
                                    ...(index !== 0
                                      ? [
                                          {
                                            label:
                                              translate("boxAction.moveUp"),
                                            onClick: () =>
                                              move(index, index - 1),
                                          },
                                        ]
                                      : []),
                                    ...(index !== fields.length - 1
                                      ? [
                                          {
                                            label:
                                              translate("boxAction.moveDown"),
                                            onClick: () =>
                                              move(index, index + 1),
                                          },
                                        ]
                                      : []),
                                    {
                                      label: translate("boxAction.duplicate"),
                                      onClick: () =>
                                        append({
                                          title: field.title,
                                          description: field.description,
                                        }),
                                    },
                                    {
                                      label: translate("boxAction.delete"),
                                      onClick: () => remove(index),
                                    },
                                  ]}
                                />
                              }
                              headerProps={{ ...provided.dragHandleProps }}
                            >
                              <div className="flex flex-col gap-y-4 px-4 py-4 max-h-[750px] overflow-y-auto sm:px-6">
                                <div>
                                  <Label
                                    required
                                    id={`detail_title_${index}`}
                                    name={translate("eventSetup.eventInfo.eventSection.titleName")}
                                  />
                                  <div className="flex flex-col gap-y-4">
                                    {getValues("languages").map(
                                      (lang: SupportedLanguage) => (
                                        <div key={lang}>
                                          <Controller
                                            name={`details.${index}.title.${lang}`}
                                            control={control}
                                            defaultValue={""}
                                            render={({ field }: any) => (
                                              <div className="relative">
                                                <Input
                                                  id={`detail_title_${index}`}
                                                  type="text"
                                                  placeholder={`${translate("eventSetup.eventInfo.eventSection.titleName")} (${lang})`}
                                                  className="pr-11"
                                                  error={get(
                                                    errors.details,
                                                    `${index}.title.${lang}.message`,
                                                    ""
                                                  )}
                                                  {...field}
                                                />
                                                <img
                                                  src={`/images/flags/circle-${lang}.png`}
                                                  alt="Language Icon"
                                                  className="absolute top-1/2 right-3 -translate-y-1/2"
                                                />
                                              </div>
                                            )}
                                          />
                                          {get(
                                            errors,
                                            `details.${index}.title.${lang}`,
                                            ""
                                          ) ? (
                                            <p className="error-text">
                                              <span className="font-medium">
                                                {get(
                                                  errors,
                                                  `details.${index}.title.${lang}.message`,
                                                  ""
                                                )}
                                              </span>
                                            </p>
                                          ) : null}
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                                <div>
                                  <Label
                                    id={`details.${index}.description`}
                                    name={translate("eventSetup.eventInfo.eventSection.description")}
                                  />
                                  <div className="flex flex-col gap-y-4">
                                    {getValues("languages").map(
                                      (lang: SupportedLanguage) => (
                                        <div key={lang}>
                                          <div
                                            className={`w-fit flex flex-row items-center gap-x-1 p-2 -mb-1 rounded-tl-md rounded-tr-md border-magnolia-600 border-solid border-[1px] bg-magnolia-400 font-medium text-sm`}
                                          >
                                            <img
                                              src={`/images/flags/circle-${lang}.png`}
                                              alt="Language Icon"
                                              className="w-4 shrink-0"
                                            />
                                            <p>
                                              {
                                                LANGUAGES.find(
                                                  (language) =>
                                                    language.value === lang
                                                )?.label
                                              }
                                            </p>
                                          </div>

                                          <Controller
                                            control={control}
                                            name={`details.${index}.description.${lang}`}
                                            render={({
                                              field: { onChange, value = "" },
                                            }) => (
                                              <TextEditor
                                                key={`details.${index}.description.${lang}`}
                                                value={value ?? ""}
                                                placeholder={`${translate("eventSetup.eventInfo.eventSection.description")} (${lang})`}
                                                onChange={onChange}
                                              />
                                            )}
                                          />

                                          {get(
                                            errors,
                                            `details.${index}.description.${lang}`,
                                            ""
                                          ) ? (
                                            <p className="error-text">
                                              <span className="font-medium">
                                                {get(
                                                  errors,
                                                  `details.${index}.description.${lang}.message`,
                                                  ""
                                                )}
                                              </span>
                                            </p>
                                          ) : null}
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              </div>
                            </CollapsibleSection>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </DragDropContext>
          <div className="w-full text-center">
            <button
              type="button"
              className="flex flex-row items-center mx-auto mt-4 w-fit py-2.5 px-4 text-sm bg-white text-brand-500 active:bg-gray-50"
              onClick={() =>
                append({
                  title: { en: "", vi: "" },
                  description: { en: "", vi: "" },
                })
              }
            >
              <PlusCircleIcon className="w-5 h-5 mr-1.5" />{" "}
              <span className="shrink-0 text-sm">
                {translate("eventSetup.eventInfo.eventSection.addMoreSection")}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
